import React, { useEffect, useState } from 'react';
import { Button, Input, Spin } from 'antd';
import { SearchOutlined, DownOutlined, SortAscendingOutlined, InboxOutlined, ReloadOutlined } from '@ant-design/icons';
import { Header, CountRow, List, LoadMore, Toolbar, FilterRow, EmptyState } from '../ProductMobileList/ProductMobileList.style';

interface MobileListLayoutProps {
  title: string;
  action?: React.ReactNode;
  search: string;
  onSearch: (value: string) => void;
  searchPlaceholder: string;
  filters?: React.ReactNode;
  count: string;
  sortLabel?: string;
  onSortOpen?: () => void;
  loading: boolean;
  isEmpty: boolean;
  emptyTitle: string;
  emptyText: string;
  onReset?: () => void;
  hasMore?: boolean;
  onLoadMore?: () => void;
  children: React.ReactNode;
}

const MobileListLayout: React.FC<MobileListLayoutProps> = ({
  title, action, search, onSearch, searchPlaceholder, filters, count, sortLabel, onSortOpen,
  loading, isEmpty, emptyTitle, emptyText, onReset, hasMore, onLoadMore, children,
}) => {
  const [input, setInput] = useState(search);

  useEffect(() => {
    setInput(search);
  }, [search]);

  // Debounce typing so the list is not reloaded on every key stroke
  useEffect(() => {
    if (input.trim() === search) return undefined;
    const timer = window.setTimeout(() => onSearch(input.trim()), 400);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  return (
    <div>
      <Header>
        <h1>{title}</h1>
        {action}
      </Header>
      <Toolbar>
        <Input
          prefix={<SearchOutlined />}
          placeholder={searchPlaceholder}
          allowClear
          value={input}
          onChange={(e) => setInput(e.target.value)}
          data-testid="mobile-search"
        />
        {filters && <FilterRow>{filters}</FilterRow>}
      </Toolbar>
      <CountRow>
        <span data-testid="mobile-count">{count}</span>
        {onSortOpen && (
          <Button type="text" icon={<SortAscendingOutlined />} onClick={onSortOpen} data-testid="mobile-sort-open">
            {sortLabel} <DownOutlined />
          </Button>
        )}
      </CountRow>
      <List>{children}</List>
      {!loading && isEmpty && (
        <EmptyState data-testid="mobile-empty-state">
          <InboxOutlined />
          <h3>{emptyTitle}</h3>
          <span>{emptyText}</span>
          {onReset && <Button icon={<ReloadOutlined />} onClick={onReset} data-testid="mobile-empty-reset">Filter zurücksetzen</Button>}
        </EmptyState>
      )}
      {loading && <LoadMore><Spin /></LoadMore>}
      {!loading && hasMore && onLoadMore && (
        <LoadMore>
          <Button onClick={onLoadMore} data-testid="mobile-load-more">Mehr laden</Button>
        </LoadMore>
      )}
    </div>
  );
};

export default MobileListLayout;
